"use client";

import { useReducedMotion } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib";

/** Default full scale cycle; theme `--pulse-scale-duration` matches when unset. */
export const PULSE_SCALE_DEFAULT_DURATION_S = 3.6;

interface PulseScaleMotionProps {
	children: ReactNode;
	className?: string;
	/** Custom full scale cycle in seconds. Applies inline when set (theme utility ignores per-instance duration). */
	durationSec?: number;
	/** Seconds to offset the animation start (desyncs multiple pulsers). */
	delaySec?: number;
}

/**
 * Repeating ease-in-out scale in/out (`animate-pulse-scale`). Wrap content whose positioning
 * transform stays on an ancestor so `scale` here is not overwritten.
 *
 * For MagicCards hover/carousel-gated pulsing (idle at min scale), use
 * `MAGIC_CARD_PULSE_SCALE_CLASS` instead — CSS drives play under the same rules as colorize.
 */
export function PulseScaleMotion({
	children,
	className,
	durationSec,
	delaySec = 0,
}: PulseScaleMotionProps) {
	const reducedMotion = useReducedMotion();
	const useCustomDuration = durationSec != null && !reducedMotion;
	const style: CSSProperties | undefined = useCustomDuration
		? {
				animation: `pulse-scale ${durationSec}s ease-in-out infinite`,
				animationDelay: `${delaySec}s`,
			}
		: durationSec == null
			? ({
					"--pulse-scale-duration": `${PULSE_SCALE_DEFAULT_DURATION_S}s`,
					...(delaySec !== 0
						? { animationDelay: `${delaySec}s` }
						: {}),
				} as CSSProperties)
			: undefined;

	return (
		<div
			className={cn(
				"origin-center motion-safe:will-change-transform motion-reduce:animate-none",
				!useCustomDuration &&
					durationSec == null &&
					"motion-safe:animate-pulse-scale",
				className,
			)}
			style={style}
		>
			{children}
		</div>
	);
}
